/**
 * MyTime — a fee earner's own time-recording home (time-recording-
 * foundations, Ticket 5). Log time against any matter, see everything
 * you've recorded, edit or delete a draft entry inline. No submit/approval
 * step: Vanessa, 26 Sep 2026, "if we can edit and delete entries, we don't
 * need submit etc, this was cumbersome on lawbillity."
 *
 * Clocks (start/pause/resume/finish) ALSO live here, on top of the
 * globally-mounted FloatingClockWidget (AppLayout.jsx). 29 Sep 2026 moved
 * them off this page entirely ("i thought stop start clocks were pop outs,
 * no?"); 1 Oct 2026, after reviewing against a LawBillity reference,
 * Vanessa asked for both back -- the pop-out for reachability from any
 * page, this card for full management without leaving /my-time. Both mount
 * the same TimeClockList and each polls GET /time-clocks independently
 * (see that component's own docstring for why). Finishing a clock in
 * either place dispatches a `time-entry-logged` window event, which is
 * what the listener below picks up to refresh "My entries".
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, ListChecks } from "lucide-react";
import TimeEntryForm from "../components/TimeEntryForm";
import TimeEntryList from "../components/TimeEntryList";
import TimeClockList from "../components/TimeClockList";

export default function MyTime() {
  // TimeEntryList re-fetches on mount; bumping its key after the sibling
  // form saves a new entry (or a clock is finished elsewhere) forces that
  // re-fetch without lifting the entries themselves into this page.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const onLogged = () => setRefreshKey((k) => k + 1);
    window.addEventListener("time-entry-logged", onLogged);
    return () => window.removeEventListener("time-entry-logged", onLogged);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#060e18" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 24px 0" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#f9fafb", margin: 0 }} className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-teal" />
            My Time
          </h1>
          {/* BM-H3628: Vanessa, "button at the top of the My Time logging page" -- the only way in; deliberately not a sidebar item (menu is cluttered). */}
          <Link
            to="/all-time-entries"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-teal text-teal text-sm font-medium hover:bg-teal hover:text-navy"
            data-testid="my-time-all-entries-button"
          >
            <ListChecks className="w-4 h-4" />
            All My Time Entries
          </Link>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 24px 80px" }}>
        <section
          className="bg-white/[0.02] border border-white/10 rounded-lg p-5 mb-6"
          data-testid="my-time-clocks-card"
        >
          <h2 className="text-base font-semibold text-white mb-4">Clocks</h2>
          <TimeClockList onFinished={() => setRefreshKey((k) => k + 1)} />
        </section>

        <section
          className="bg-white/[0.02] border border-white/10 rounded-lg p-5 mb-6"
          data-testid="my-time-form-card"
        >
          <h2 className="text-base font-semibold text-white mb-4">Log time</h2>
          <TimeEntryForm onSaved={() => setRefreshKey((k) => k + 1)} />
        </section>

        <section
          className="bg-white/[0.02] border border-white/10 rounded-lg p-5"
          data-testid="my-time-list-card"
        >
          <h2 className="text-base font-semibold text-white mb-4">My entries</h2>
          <TimeEntryList key={refreshKey} showMatterColumn mine />
        </section>
      </div>
    </div>
  );
}
